/**
 * Leviathan - Users List Page
 * Quasar Prime LLC
 *
 * Administrator's view of who has access to this instance, and the controls to
 * change it.
 *
 * The API has had these routes all along, correctly behind `requireAdmin`, and
 * the web app had no controls for any of them. So an admin could not remove a
 * user without curl — which meant that when a customer fired an employee, they
 * could not revoke that person's access to their entire book of business
 * themselves. Everything below exists to close that.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Check,
  Copy,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  Trash2,
  Users as UsersIcon,
  X,
} from 'lucide-react';

import { API_URL } from '@/config/api';
import { parseApiError } from '@/utils/errorHandler';
import { ROLE_OPTIONS, isAdminRole, roleLabel } from '@/constants/userRoles';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  loginCount: number;
  createdAt: string;
  /** UserRole enum — the access level the API actually enforces. */
  role: string;
  /** Named permission group. Additive: it can only ADD to the role. */
  roleGroup: { id: string; name: string } | null;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
  isExpired: boolean;
  inviteUrl: string;
  invitedBy: { firstName: string | null; lastName: string | null; email: string } | null;
}

type Banner = { type: 'success' | 'error'; text: string } | null;

export default function Users() {
  const navigate = useNavigate();
  const { accessToken, user: currentUser } = useAuth();
  const isAdmin = isAdminRole(currentUser?.role);

  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);

  // Confirmation + result dialogs. Nothing destructive happens on a bare click.
  const [confirming, setConfirming] = useState<{ user: User; action: 'deactivate' | 'reset' } | null>(
    null
  );
  const [busy, setBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState<{ user: User; password: string } | null>(null);

  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('USER');
  const [copied, setCopied] = useState<string | null>(null);

  const authHeaders = useCallback(
    (json = false): Record<string, string> => ({
      Authorization: `Bearer ${accessToken}`,
      ...(json ? { 'Content-Type': 'application/json' } : {}),
    }),
    [accessToken]
  );

  const fetchUsers = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (!showInactive) params.append('isActive', 'true');

      const response = await fetch(`${API_URL}/api/users?${params}`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, authHeaders, search, showInactive]);

  const fetchInvitations = useCallback(async () => {
    // Admin-only on the server, because the response carries invitation tokens.
    // Asking as a non-admin would 403; not asking keeps the console clean.
    if (!accessToken || !isAdmin) return;
    try {
      const response = await fetch(`${API_URL}/api/settings/invitations`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations);
      }
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    }
  }, [accessToken, authHeaders, isAdmin]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const copyLink = async (invitation: Invitation) => {
    try {
      await navigator.clipboard.writeText(invitation.inviteUrl);
      setCopied(invitation.id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setBanner({ type: 'error', text: 'Could not copy. Select the link and copy it manually.' });
    }
  };

  const setActive = async (user: User, isActive: boolean) => {
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ isActive }),
      });
      if (!response.ok) throw new Error(await parseApiError(response));

      setBanner({
        type: 'success',
        text: isActive
          ? `${user.email} can sign in again.`
          : `${user.email} can no longer sign in. Their history is untouched.`,
      });
      setConfirming(null);
      await fetchUsers();
    } catch (error) {
      setBanner({ type: 'error', text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (user: User) => {
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(await parseApiError(response));

      const data = await response.json();
      setConfirming(null);
      setTempPassword({ user, password: data.tempPassword });
    } catch (error) {
      setBanner({ type: 'error', text: (error as Error).message });
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  };

  const sendInvitation = async () => {
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/invitations`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      if (!response.ok) throw new Error(await parseApiError(response));

      setInviting(false);
      setInviteEmail('');
      setInviteRole('USER');
      setBanner({
        type: 'success',
        text: 'Invitation created. Copy its link below and send it to them — nothing is emailed.',
      });
      await fetchInvitations();
    } catch (error) {
      setBanner({ type: 'error', text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const resendInvitation = async (invitation: Invitation) => {
    setBusy(true);
    try {
      const response = await fetch(
        `${API_URL}/api/settings/invitations/${invitation.id}/resend`,
        { method: 'POST', headers: authHeaders() }
      );
      if (!response.ok) throw new Error(await parseApiError(response));

      setBanner({
        type: 'success',
        text: `New link issued for ${invitation.email}. The previous link no longer works — copy the new one.`,
      });
      await fetchInvitations();
    } catch (error) {
      setBanner({ type: 'error', text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const cancelInvitation = async (invitation: Invitation) => {
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/invitations/${invitation.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(await parseApiError(response));

      setBanner({ type: 'success', text: `Invitation for ${invitation.email} cancelled.` });
      await fetchInvitations();
    } catch (error) {
      setBanner({ type: 'error', text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 via-blue-500 to-amber-500" />
          <h1 className="text-3xl font-bold text-slate-100 pl-6">Users</h1>
          <p className="text-slate-400 text-sm pl-6 mt-1">
            {total} users &middot; System access management
          </p>
        </div>

        {isAdmin && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setInviting(true)}
              className="flex items-center space-x-2 border border-amber-500 text-amber-500 hover:bg-amber-500/10 font-medium py-2 px-4 transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span>Invite User</span>
            </button>
            <Link
              to="/admin/users/new"
              className="flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium py-2 px-4 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add User</span>
            </Link>
          </div>
        )}
      </div>

      {banner && (
        <div
          className={`flex items-start justify-between p-4 border ${
            banner.type === 'success'
              ? 'bg-green-500/10 border-green-500/50 text-green-300'
              : 'bg-red-500/10 border-red-500/50 text-red-300'
          }`}
        >
          <span className="text-sm">{banner.text}</span>
          <button onClick={() => setBanner(null)} className="ml-4 opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {!isAdmin && (
        <div className="flex items-start space-x-3 p-4 bg-slate-800 border border-slate-700 text-slate-400 text-sm">
          <ShieldAlert className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <span>
            You can see who has access, but only an administrator can change it. The controls are
            hidden here and refused by the server either way.
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 pl-10 pr-4 py-2 text-slate-100 text-sm"
          />
        </div>

        <label className="flex items-center space-x-2 text-slate-400 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-slate-600 bg-slate-900 text-amber-500 focus:ring-amber-500"
          />
          <span>Show Deactivated</span>
        </label>

        <Link to="/admin/roles" className="text-amber-500 hover:text-amber-400 text-sm">
          Permission Groups &rarr;
        </Link>
      </div>

      {/* Users Table */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber-500" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber-500" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber-500" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber-500" />

        <div className="bg-slate-800 border border-slate-700 overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
              <div className="text-slate-400 mb-4">No users found</div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-900 border-b border-slate-700">
                <tr>
                  <Th>User</Th>
                  <Th>Email</Th>
                  <Th>Access Level</Th>
                  <Th center>Last Login</Th>
                  <Th center>Status</Th>
                  {isAdmin && <Th right>Actions</Th>}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isSelf = user.id === currentUser?.id;
                  return (
                    <tr key={user.id} className="border-b border-slate-700 hover:bg-amber-500/5">
                      <td
                        className="px-4 py-3 cursor-pointer"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        <div className="text-slate-100 font-medium">
                          {user.firstName} {user.lastName}
                          {isSelf && <span className="ml-2 text-xs text-amber-500">(you)</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm">{user.email}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 text-xs border ${
                            isAdminRole(user.role)
                              ? 'bg-amber-500/20 text-amber-400 border-amber-500/50'
                              : 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                          }`}
                        >
                          {roleLabel(user.role)}
                        </span>
                        {user.roleGroup && (
                          <span className="ml-2 text-xs text-slate-500">{user.roleGroup.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-400 text-sm">
                        {formatDate(user.lastLoginAt)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-1 text-xs border ${
                            user.isActive
                              ? 'bg-green-500/20 text-green-400 border-green-500/50'
                              : 'bg-slate-500/20 text-slate-400 border-slate-500/50'
                          }`}
                        >
                          {user.isActive ? 'ACTIVE' : 'DEACTIVATED'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end space-x-2 whitespace-nowrap">
                            <RowAction to={`/admin/users/${user.id}/edit`}>Edit</RowAction>
                            <RowAction to={`/admin/users/${user.id}/permissions`}>
                              Permissions
                            </RowAction>
                            <RowAction onClick={() => setConfirming({ user, action: 'reset' })}>
                              Reset Password
                            </RowAction>
                            {user.isActive ? (
                              <RowAction
                                danger
                                disabled={isSelf}
                                title={
                                  isSelf ? 'You cannot deactivate your own account' : undefined
                                }
                                onClick={() => setConfirming({ user, action: 'deactivate' })}
                              >
                                Deactivate
                              </RowAction>
                            ) : (
                              <RowAction onClick={() => setActive(user, true)}>
                                Reactivate
                              </RowAction>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pending invitations */}
      {isAdmin && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-xl font-bold text-slate-100">Pending Invitations</h2>
            <span className="text-xs text-slate-500">
              Nothing is emailed — copy each link and send it yourself.
            </span>
          </div>

          <div className="bg-slate-800 border border-slate-700">
            {invitations.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                No pending invitations.
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <Th>Email</Th>
                    <Th>Access Level</Th>
                    <Th>Invitation Link</Th>
                    <Th center>Expires</Th>
                    <Th right>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invitation) => (
                    <tr key={invitation.id} className="border-b border-slate-700">
                      <td className="px-4 py-3 text-slate-100 text-sm">{invitation.email}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/50">
                          {roleLabel(invitation.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <div className="flex items-center space-x-2">
                          <code className="text-[11px] text-slate-500 truncate" title={invitation.inviteUrl}>
                            {invitation.inviteUrl}
                          </code>
                          <button
                            onClick={() => copyLink(invitation)}
                            className="text-amber-500 hover:text-amber-400 flex-shrink-0"
                            title="Copy invitation link"
                          >
                            {copied === invitation.id ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        {invitation.isExpired ? (
                          <span className="text-red-400">Expired</span>
                        ) : (
                          <span className="text-slate-400">{formatDate(invitation.expiresAt)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end space-x-2 whitespace-nowrap">
                          <RowAction
                            disabled={busy}
                            onClick={() => resendInvitation(invitation)}
                            title="Issue a new link. The old one stops working."
                          >
                            <span className="flex items-center space-x-1">
                              <RefreshCw className="w-3 h-3" />
                              <span>New Link</span>
                            </span>
                          </RowAction>
                          <RowAction danger disabled={busy} onClick={() => cancelInvitation(invitation)}>
                            <span className="flex items-center space-x-1">
                              <Trash2 className="w-3 h-3" />
                              <span>Cancel</span>
                            </span>
                          </RowAction>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Invite modal */}
      {inviting && (
        <Modal title="Invite a User" onClose={() => setInviting(false)}>
          <p className="text-slate-400 text-sm mb-4">
            This creates an invitation link. <strong className="text-amber-500">No email is
            sent</strong> — you will copy the link from the list and send it to them yourself.
          </p>

          <label className="block text-sm text-slate-300 mb-1">Email address</label>
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="name@company.com"
            className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm mb-1"
          />
          <p className="text-xs text-slate-500 mb-4">
            This is the address they will sign in with, and it is shown to them on the enrolment
            page. It cannot be changed after the invitation is created.
          </p>

          <label className="block text-sm text-slate-300 mb-1">Access level</label>
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm"
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1 mb-6">
            {ROLE_OPTIONS.find((r) => r.value === inviteRole)?.description}
          </p>

          <div className="flex gap-3">
            <ModalButton onClick={() => setInviting(false)} disabled={busy}>
              Cancel
            </ModalButton>
            <ModalButton primary onClick={sendInvitation} disabled={busy || !inviteEmail}>
              {busy ? 'Creating…' : 'Create Invitation'}
            </ModalButton>
          </div>
        </Modal>
      )}

      {/* Deactivate / reset confirmation */}
      {confirming && (
        <Modal
          title={confirming.action === 'deactivate' ? 'Deactivate User' : 'Reset Password'}
          onClose={() => setConfirming(null)}
        >
          {confirming.action === 'deactivate' ? (
            <>
              <p className="text-slate-300 mb-3">
                Deactivate{' '}
                <strong className="text-slate-100">
                  {confirming.user.firstName} {confirming.user.lastName}
                </strong>{' '}
                ({confirming.user.email})?
              </p>
              <ul className="text-sm text-slate-400 space-y-1 mb-6 list-disc list-inside">
                <li>They can no longer sign in, and any active session cannot be renewed.</li>
                <li>
                  Everything they created — orders, invoices, audit records — is left exactly as it
                  is. This is not a deletion.
                </li>
                <li>You can reactivate them later from this page.</li>
              </ul>
            </>
          ) : (
            <p className="text-slate-300 mb-6">
              Generate a new temporary password for{' '}
              <strong className="text-slate-100">{confirming.user.email}</strong>? Their current
              password stops working immediately. The new one is shown on screen once — nothing is
              emailed.
            </p>
          )}

          <div className="flex gap-3">
            <ModalButton onClick={() => setConfirming(null)} disabled={busy}>
              Cancel
            </ModalButton>
            <ModalButton
              primary
              danger={confirming.action === 'deactivate'}
              disabled={busy}
              onClick={() =>
                confirming.action === 'deactivate'
                  ? setActive(confirming.user, false)
                  : resetPassword(confirming.user)
              }
            >
              {busy
                ? 'Working…'
                : confirming.action === 'deactivate'
                  ? 'Deactivate'
                  : 'Reset Password'}
            </ModalButton>
          </div>
        </Modal>
      )}

      {/* Temporary password result */}
      {tempPassword && (
        <Modal title="Password Reset" onClose={() => setTempPassword(null)}>
          <p className="text-slate-400 text-sm mb-3">
            Temporary password for{' '}
            <strong className="text-slate-100">{tempPassword.user.email}</strong>:
          </p>
          <div className="bg-slate-900 border border-amber-500/50 p-4 mb-3">
            <code className="text-lg font-mono font-bold text-amber-400 break-all">
              {tempPassword.password}
            </code>
          </div>
          <p className="text-xs text-slate-500 mb-6">
            Shown once. Copy it now and pass it to them over something other than email — nothing is
            sent from here. They should change it after signing in.
          </p>
          <ModalButton primary onClick={() => setTempPassword(null)}>
            Done
          </ModalButton>
        </Modal>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- helpers */

function Th({
  children,
  center,
  right,
}: {
  children: React.ReactNode;
  center?: boolean;
  right?: boolean;
}) {
  return (
    <th
      className={`${
        center ? 'text-center' : right ? 'text-right' : 'text-left'
      } px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium`}
    >
      {children}
    </th>
  );
}

function RowAction({
  children,
  onClick,
  to,
  danger,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  to?: string;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  const className = `px-2 py-1 text-xs border transition-colors ${
    danger
      ? 'border-red-500/50 text-red-400 hover:bg-red-500/10'
      : 'border-slate-600 text-slate-300 hover:border-amber-500 hover:text-amber-500'
  } ${disabled ? 'opacity-40 cursor-not-allowed hover:border-slate-600 hover:text-slate-300' : ''}`;

  if (to) {
    return (
      <Link to={to} className={className} title={title}>
        {children}
      </Link>
    );
  }
  return (
    <button onClick={onClick} disabled={disabled} className={className} title={title}>
      {children}
    </button>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 p-6 max-w-lg w-full">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalButton({
  children,
  onClick,
  primary,
  danger,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  primary?: boolean;
  danger?: boolean;
  disabled?: boolean;
}) {
  const base = 'flex-1 px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50';
  const style = !primary
    ? 'border border-slate-600 text-slate-300 hover:bg-slate-700'
    : danger
      ? 'bg-red-600 text-white hover:bg-red-700'
      : 'bg-amber-500 text-slate-900 hover:bg-amber-600';

  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${style}`}>
      {children}
    </button>
  );
}
